import { MapPinIcon, CameraIcon, CalendarIcon, MountainIcon, CheckIcon, ImageIcon, XIcon, ChevronLeftIcon, ChevronRightIcon, AlertCircleIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { PhotoMetadata } from '@cdf/api-client-react';

interface PhotoMetadataCardProps {
  metadata: PhotoMetadata | null;
  uploading: boolean;
  error: boolean;
  onAcceptLocation: () => void;
  locationAccepted: boolean;
}

function formatGps(lat: number | null, lng: number | null): string {
  if (lat == null || lng == null) return '';
  const latDir = lat >= 0 ? 'N' : 'S';
  const lngDir = lng >= 0 ? 'E' : 'W';
  return `${Math.abs(lat).toFixed(6)}°${latDir}, ${Math.abs(lng).toFixed(6)}°${lngDir}`;
}

function formatDate(isoString: string | null): string {
  if (!isoString) return 'Unknown';
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return isoString;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return isoString;
  }
}

export function PhotoMetadataCard({ metadata, uploading, error, onAcceptLocation, locationAccepted }: PhotoMetadataCardProps) {
  if (uploading) {
    return (
      <Card className="border-dashed">
        <CardContent className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
          <span className="inline-block h-3 w-3 animate-pulse rounded-full bg-primary" />
          Extracting photo metadata…
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive/30">
        <CardContent className="flex items-center gap-2 py-3 text-sm text-destructive">
          <AlertCircleIcon className="h-4 w-4 shrink-0" />
          Photo upload failed. Please remove and try again.
        </CardContent>
      </Card>
    );
  }

  if (!metadata) {
    return null;
  }

  const hasGps = metadata.hasGps && metadata.latitude != null && metadata.longitude != null;
  const cameraInfo = [metadata.cameraMake, metadata.cameraModel].filter(Boolean).join(' ');
  const dimensions = metadata.width != null && metadata.height != null ? `${metadata.width}×${metadata.height}px` : null;

  return (
    <Card className={cn('overflow-hidden', hasGps && !locationAccepted && 'ring-1 ring-primary/30')}>
      <CardContent className="space-y-3 py-3">
        {/* GPS badge + accept button */}
        {hasGps && (
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 text-sm">
              <MapPinIcon className="h-4 w-4 text-primary" />
              <span className="font-medium">{formatGps(metadata.latitude, metadata.longitude)}</span>
              {metadata.altitude != null && (
                <Badge variant="secondary" className="text-xs">
                  {Math.round(metadata.altitude)}m
                </Badge>
              )}
            </div>
            {locationAccepted ? (
              <Badge className="gap-1 text-xs">
                <CheckIcon className="h-3 w-3" />
                Location set
              </Badge>
            ) : (
              <Button type="button" size="sm" onClick={onAcceptLocation} className="shrink-0">
                <MapPinIcon className="mr-1 h-3.5 w-3.5" />
                Use this location
              </Button>
            )}
          </div>
        )}

        {!hasGps && (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <MapPinIcon className="h-4 w-4" />
            No GPS data in this photo
          </div>
        )}

        {/* Metadata details grid */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
            <span>{formatDate(metadata.dateTimeOriginal)}</span>
          </div>
          {cameraInfo && (
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <CameraIcon className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{cameraInfo}</span>
            </div>
          )}
          {dimensions && (
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <ImageIcon className="h-3.5 w-3.5 shrink-0" />
              <span>{dimensions}</span>
            </div>
          )}
          {metadata.orientation != null && metadata.orientation !== 1 && (
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <MountainIcon className="h-3.5 w-3.5 shrink-0" />
              <span>Orientation: {metadata.orientation}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

interface PhotoThumbnailProps {
  preview: string;
  index: number;
  active: boolean;
  uploading: boolean;
  hasBlobKey: boolean;
  hasGps: boolean;
  onClick: () => void;
  onRemove: () => void;
  onMoveLeft: () => void;
  onMoveRight: () => void;
  canMoveLeft: boolean;
  canMoveRight: boolean;
}

export function PhotoThumbnail({
  preview,
  index,
  active,
  uploading,
  hasBlobKey,
  hasGps,
  onClick,
  onRemove,
  onMoveLeft,
  onMoveRight,
  canMoveLeft,
  canMoveRight,
}: PhotoThumbnailProps) {
  return (
    <div className="group relative">
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'relative block h-20 w-20 overflow-hidden rounded-lg border-2 transition-all',
          active ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:border-muted-foreground/40'
        )}
      >
        <img src={preview} alt={`Photo ${index + 1}`} className="h-full w-full object-cover" />
        {uploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
          </div>
        )}
        {hasBlobKey && !uploading && (
          <div className="absolute right-1 top-1 rounded-full bg-green-500 p-0.5">
            <CheckIcon className="h-2.5 w-2.5 text-white" />
          </div>
        )}
        {hasGps && (
          <div className="absolute bottom-1 left-1 rounded-full bg-primary p-0.5">
            <MapPinIcon className="h-2.5 w-2.5 text-primary-foreground" />
          </div>
        )}
      </button>
      {/* Remove button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="absolute -right-1.5 -top-1.5 rounded-full bg-destructive p-0.5 text-destructive-foreground shadow-sm opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
        aria-label={`Remove photo ${index + 1}`}
      >
        <XIcon className="h-3 w-3" />
      </button>
      {/* Reorder controls */}
      {active && (
        <div className="absolute -left-1.5 top-1/2 flex -translate-y-1/2 flex-col gap-0.5">
          {canMoveLeft && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onMoveLeft();
              }}
              className="rounded-full bg-background/90 p-0.5 shadow-sm hover:bg-background"
              aria-label="Move photo left"
            >
              <ChevronLeftIcon className="h-3 w-3" />
            </button>
          )}
          {canMoveRight && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onMoveRight();
              }}
              className="rounded-full bg-background/90 p-0.5 shadow-sm hover:bg-background"
              aria-label="Move photo right"
            >
              <ChevronRightIcon className="h-3 w-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
